import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { resolveMutationErrorMessage } from "@/shared/api/error-message";
import { useDeactivateUser } from "./use-deactivate-user";
import { resolveSelfGuard, shouldBlockSelfAction } from "./self-guard";
import type { UserDetail } from "./types";

interface DeactivateUserDialogProps {
  readonly user: UserDetail | null;
  /**
   * Firebase UID of the currently signed-in admin (from
   * `SessionUser.uid`). Defense-in-depth: even if the caller forgets
   * to hide the entry-point button for the self case, this dialog
   * refuses to submit. Same rationale as `ChangeRoleDialog` — compared
   * against `user.firebaseUid`, NEVER `user.id` (namespace mismatch).
   */
  readonly currentAdminFirebaseUid: string | null;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSuccess: () => void;
}

export function DeactivateUserDialog({
  user,
  currentAdminFirebaseUid,
  open,
  onOpenChange,
  onSuccess,
}: DeactivateUserDialogProps) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const mutation = useDeactivateUser();

  useEffect(() => {
    if (open) setError(null);
  }, [open, user?.id]);

  if (!user) return null;

  // See self-guard.ts — 'unverified' also blocks, so a missing
  // firebaseUid from the backend can't fail-open.
  const guard = resolveSelfGuard(currentAdminFirebaseUid, user.firebaseUid);
  const isBlocked = shouldBlockSelfAction(guard);

  const handleOpenChange = (next: boolean) => {
    if (!next && mutation.isPending) return;
    onOpenChange(next);
  };

  const handleConfirm = () => {
    if (isBlocked) return;
    setError(null);
    mutation.mutate(user.id, {
      onSuccess: () => {
        onOpenChange(false);
        onSuccess();
      },
      onError: (err) => {
        setError(
          resolveMutationErrorMessage(err, t, {
            forbiddenKey: "users.errors.forbidden",
            serverPrefixKey: "users.errors.serverPrefix",
            genericKey: "users.deactivate.error",
          }),
        );
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="deactivate-user-dialog">
        <DialogHeader>
          <DialogTitle>{t("users.deactivate.title")}</DialogTitle>
          <DialogDescription>
            {t("users.deactivate.description", {
              name: user.displayName || user.email,
            })}
          </DialogDescription>
        </DialogHeader>

        {guard === "self" && (
          <div
            data-testid="deactivate-user-self-warning"
            className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3"
          >
            <AlertTriangle
              className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600"
              aria-hidden="true"
            />
            <p className="font-sans text-xs font-normal text-amber-900 dark:text-amber-200">
              {t("users.deactivate.selfWarning")}
            </p>
          </div>
        )}

        {guard === "unverified" && (
          <div
            data-testid="deactivate-user-unverified-warning"
            className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3"
          >
            <AlertTriangle
              className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600"
              aria-hidden="true"
            />
            <p className="font-sans text-xs font-normal text-amber-900 dark:text-amber-200">
              {t("users.unverifiedWarning")}
            </p>
          </div>
        )}

        {error && (
          <p
            data-testid="deactivate-user-error"
            className="font-sans text-sm font-medium text-destructive"
          >
            {error}
          </p>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t("users.actions.cancel")}
          </Button>
          <Button
            data-testid="confirm-deactivate-user-button"
            variant="destructive"
            onClick={handleConfirm}
            disabled={isBlocked || mutation.isPending}
          >
            {mutation.isPending ? t("users.deactivate.pending") : t("users.deactivate.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
